/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * Analyze the link and determine its type and potential internal path.
 * @param {string} link - The link to analyze.
 * @returns {Object} - Returns an object containing the internal
 * link URL and a flag indicating if it's an external link.
 */
export const analyzeLink = (link: string) => {
  const baseDomain = getBaseDomain();

  // Check if the current origin is relevant for processing
  if (!isRelevantOrigin()) {
    return {
      internalLinkUrl: link,
      isExternalLink: !isInternalLink(link, baseDomain),
    };
  }

  return {
    internalLinkUrl: getInternalLinkPath(link, baseDomain),
    isExternalLink: !isInternalLink(link, baseDomain),
  };
};

/**
 * Generate the base domain using environment variables.
 * @returns {string} - Returns the concatenated base domain.
 */
const getBaseDomain = (): string => `${process.env.GATSBY_BASE_DOMAIN}.${process.env.GATSBY_ROCHE_TOP_LEVEL_DOMAIN}`;

/**
 * Determine if the current origin matches the predefined relevant origins.
 * @returns {boolean} - True if it's a relevant origin, otherwise false.
 */
const isRelevantOrigin = (): boolean => {
  const { host } = new URL(window.location.href);
  return host.includes('preview') || host.includes('live');
};

/**
 * Check if the link's hostname matches the expected patterns.
 * @param {string} link - The link to check.
 * @param {string} baseDomain - The base domain for comparison.
 * @returns {boolean} - True if hostname matches, otherwise false.
 */
const isHostnameMatching = (link: string, baseDomain: string): boolean => {
  try {
    const url = new URL(link);
    return url.hostname === `www.${baseDomain}` || url.hostname === `preview.${baseDomain}` || url.hostname === `live.${baseDomain}` || url.hostname === `${baseDomain}`;
  } catch (error) {
    return false; // return false if the link is not a valid URL
  }
};

/**
 * Extract the internal link path from the provided link.
 * @param {string} link - The link from which the path will be extracted.
 * @param {string} baseDomain - The base domain for comparison.
 * @returns {string} - Returns the extracted internal path or the link itself.
 */
const getInternalLinkPath = (link: string, baseDomain: string): string => {
  if (!link) return '';
  try {
    const url = new URL(link);
    return isHostnameMatching(link, baseDomain) ? url.pathname : link;
  } catch (error) {
    return link; // return the link as is if it's not a valid URL
  }
};

/**
 * Determine if the provided link is an internal link.
 * @param {string} link - The link to check.
 * @param {string} baseDomain - The base domain for comparison.
 * @returns {boolean} - True if it's an internal link, otherwise false.
 */
const isInternalLink = (link: string, baseDomain: string): boolean => {
  if (!link) return false;
  if (link.startsWith('/')) return true;

  try {
    const url = new URL(link);
    return url.hostname.endsWith(baseDomain) && isHostnameMatching(link, baseDomain);
  } catch (error) {
    return false; // return false if the link is not a valid URL
  }
};
